import { Temporal } from '@js-temporal/polyfill'
import { createNextState } from '@reduxjs/toolkit'

import { RPCClient } from '@buffer-mono/legacy-rpc-utils'

import type { Tag as TagType } from '~publish/gql/graphql'

export type Post = {
  profile_ids: string[]
  text?: string
  media?: { photo?: string }
  tags: TagType[]
  created_source: 'bulk_upload'
  top?: boolean
  scheduled_at?: number
  postingTime: string
}

export type ListItem = {
  id: string
  status: 'valid' | 'loading' | 'success' | 'error'
  errors?: string[]
  data: Post
}

export type CreatePostResponse = {
  success: boolean
  message?: string
  code?: number
}

export const isError = (item: ListItem): boolean => item.status === 'error'
export const isSuccess = (item: ListItem): boolean => item.status === 'success'
export const isValid = (item: ListItem): boolean => item.status === 'valid'

export function isCreatePostResponse(obj: unknown): obj is CreatePostResponse {
  if (
    typeof obj === 'object' &&
    obj !== null &&
    'success' in obj &&
    typeof obj.success === 'boolean'
  ) {
    return true
  }
  return false
}

const rpc = new RPCClient({
  url: '/rpc',
  sendCredentials: 'same-origin',
})

export const createPost = async (
  post: Omit<Post, 'postingTime'>,
): Promise<CreatePostResponse> => {
  const response = await rpc.call('composerApiProxy', {
    url: '/1/updates/create.json',
    args: post,
    HTTPMethod: 'POST',
  })
  if (isCreatePostResponse(response)) {
    return response
  }
  throw new Error('Invalid response from createPost')
}

export const updateItemStatus = (
  items: ListItem[],
  id: string,
  status: ListItem['status'],
  errors?: string[],
): ListItem[] => {
  return createNextState(items, (draft) => {
    const item = draft.find((item) => item.id === id)
    if (item) {
      item.status = status
      if (errors) item.errors = errors
    }
    return draft
  })
}

export const parsePostingTime = (
  postingTime: string,
  channelTimezone: string,
): { top?: boolean; scheduled_at?: number } => {
  if (postingTime === 'Prioritize') {
    return { top: true }
  }

  if (postingTime === 'Next Available' || !postingTime) {
    return {}
  }

  const isoString = postingTime.replace(' ', 'T')
  const plainDateTime = Temporal.PlainDateTime.from(isoString)
  const zonedDateTime = plainDateTime.toZonedDateTime(channelTimezone)

  return { scheduled_at: zonedDateTime.epochSeconds }
}
